"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Table, Trash2, X } from "lucide-react";
import Markdown from "@/components/Markdown";
import { AUTO_COLORS, COLOR_HEX, type BlockColor } from "@/lib/ile/palette";
import { MAX_COLORED_COLUMNS, tableToMarkdown, type TableModel } from "@/lib/lesson-content/table-markdown";

const MAX_COLUMNS = 8;
const MAX_ROWS = 30;
const cellClass =
  "w-full min-w-[8rem] rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100";

/**
 * The lesson builder's table tool: edit a table as a grid (cells, rows, columns, a colour per
 * column, striped rows) with a live preview. Saving hands back the markdown the lesson stores.
 */
export default function TableEditor({
  initial,
  isEdit,
  onSave,
  onClose,
}: {
  initial: TableModel;
  isEdit: boolean;
  onSave: (markdown: string) => void;
  onClose: () => void;
}) {
  const [table, setTable] = useState<TableModel>(initial);
  const width = table.headers.length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setHeader = (c: number, v: string) => setTable((t) => ({ ...t, headers: t.headers.map((h, i) => (i === c ? v : h)) }));
  const setCell = (r: number, c: number, v: string) =>
    setTable((t) => ({ ...t, rows: t.rows.map((row, ri) => (ri === r ? row.map((x, ci) => (ci === c ? v : x)) : row)) }));
  const setColor = (c: number, v: BlockColor | null) => setTable((t) => ({ ...t, colors: t.colors.map((x, i) => (i === c ? v : x)) }));
  const addRow = () => setTable((t) => ({ ...t, rows: [...t.rows, Array(t.headers.length).fill("")] }));
  const removeRow = (r: number) => setTable((t) => ({ ...t, rows: t.rows.filter((_, i) => i !== r) }));
  const addColumn = () =>
    setTable((t) => ({
      ...t,
      headers: [...t.headers, `Column ${t.headers.length + 1}`],
      rows: t.rows.map((row) => [...row, ""]),
      colors: [...t.colors, null],
      align: t.align ? [...t.align, null] : undefined,
    }));
  const removeColumn = (c: number) =>
    setTable((t) => ({
      ...t,
      headers: t.headers.filter((_, i) => i !== c),
      rows: t.rows.map((row) => row.filter((_, i) => i !== c)),
      colors: t.colors.filter((_, i) => i !== c),
      align: t.align?.filter((_, i) => i !== c),
    }));

  const markdown = tableToMarkdown(table);

  // Rendered on <body>: the editor often sits inside a <label>, which would turn a click on
  // blank space here into a click on the toolbar's first button (Bold).
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Table">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Table className="h-5 w-5 text-indigo-600" />
            <h2 className="text-base font-semibold text-slate-900">{isEdit ? "Edit table" : "Insert table"}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-5 overflow-y-auto px-5 py-4">
          <div className="shrink-0 overflow-x-auto">
            <table className="w-full border-separate border-spacing-1.5">
              <thead>
                <tr>
                  {table.headers.map((_, c) => {
                    const color = table.colors[c];
                    const canColor = c < MAX_COLORED_COLUMNS;
                    return (
                      <th key={c} className="text-left align-bottom font-normal">
                        <div className="flex items-center gap-1">
                          <span className="h-4 w-4 shrink-0 rounded-full border border-slate-300" style={{ background: color ? COLOR_HEX[color].head : "transparent" }} aria-hidden />
                          <select
                            value={color ?? ""}
                            disabled={!canColor}
                            onChange={(e) => setColor(c, (e.target.value || null) as BlockColor | null)}
                            title={canColor ? "Column colour" : `Only the first ${MAX_COLORED_COLUMNS} columns can be coloured`}
                            aria-label={`Column ${c + 1} colour`}
                            className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-700 disabled:opacity-50"
                          >
                            <option value="">No colour</option>
                            {AUTO_COLORS.map((k) => (
                              <option key={k} value={k}>
                                {k[0].toUpperCase() + k.slice(1)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => removeColumn(c)}
                            disabled={width <= 1}
                            title="Delete column"
                            aria-label={`Delete column ${c + 1}`}
                            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </th>
                    );
                  })}
                  <th />
                </tr>
                <tr>
                  {table.headers.map((h, c) => (
                    <th key={c} className="font-normal">
                      <input value={h} onChange={(e) => setHeader(c, e.target.value)} aria-label={`Header ${c + 1}`} className={`${cellClass} font-semibold`} />
                    </th>
                  ))}
                  <th>
                    <button
                      type="button"
                      onClick={addColumn}
                      disabled={width >= MAX_COLUMNS}
                      className="flex items-center gap-1 whitespace-nowrap rounded-md border border-dashed border-slate-300 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" /> Column
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c}>
                        <input value={cell} onChange={(e) => setCell(r, c, e.target.value)} aria-label={`Row ${r + 1}, column ${c + 1}`} className={cellClass} />
                      </td>
                    ))}
                    <td>
                      <button
                        type="button"
                        onClick={() => removeRow(r)}
                        title="Delete row"
                        aria-label={`Delete row ${r + 1}`}
                        className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={addRow}
              disabled={table.rows.length >= MAX_ROWS}
              className="flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" /> Row
            </button>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={table.stripes} onChange={(e) => setTable((t) => ({ ...t, stripes: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
              Stripe alternate rows
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</span>
            <div className="ile-runner rounded-lg border border-slate-200 px-4 py-3">
              <Markdown content={markdown} variant="lesson" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button type="button" onClick={() => onSave(markdown)} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
            {isEdit ? "Update table" : "Insert table"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
